import {
  HashRouter, // Only HashRouter can work on production builder
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { PathConstant } from "@/const";
import { useHandleSnackbar } from "@/hooks";
import {
  DeviceListPage,
  InternalRequestListPage,
  ExternalRequestListPage,
  LoginPage,
  ProfilePage,
  ApplicationListPage,
  UserListPage,
  UserAddPage,
  IdentityResponseListPage,
  SailorResponseListPage,
  ViolationResponseListPage,
  IdentitySearchListPage,
  SailorSearchListPage,
  ShipSearchListPage,
} from "./pages";
import DemoPage from "./pages/Demo";
import MainLayout from "./layouts/MainLayout";
import { AppLoading } from "./components/common";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingCheckNetworkDialog from "@/components/common/dialog/LoadingCheckNetworkDialog";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  useHandleSnackbar();
  const { t: getLabel } = useTranslation();
  const isOnline = useNetworkStatus();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const currentPath = location.pathname === "/" && location.hash ? location.hash.replace(/^#/, "") : location.pathname;

  // lưu giá trị mạng trước đó để so sánh
  const prevIsOnline = useRef<boolean | null>(null);

  useEffect(() => {
    if (currentPath.includes(PathConstant.LOGIN)) {
      setOpen(false);
      return;
    }

    // lần đầu mount → chỉ lưu trạng thái mạng, không show gì cả
    if (prevIsOnline.current === null) {
      prevIsOnline.current = isOnline;
      return;
    }

    // chỉ chạy khi từ OFF → ON hoặc ON → OFF
    if (prevIsOnline.current !== isOnline) {
      if (isOnline) {
        // --- từ offline sang online
        queryClient.invalidateQueries();
        setTitle(getLabel("OBJ_CHECK_NETWORK.ONLINE.title1"));
        setMessage(getLabel("OBJ_CHECK_NETWORK.ONLINE.des1"));
        setOpen(true);

        const updateTimer2 = setTimeout(() => {
          setTitle(getLabel("OBJ_CHECK_NETWORK.ONLINE.title2"));
          setMessage(getLabel("OBJ_CHECK_NETWORK.ONLINE.des2"));
        }, 10000);

        const updateTimer3 = setTimeout(() => {
          setTitle(getLabel("OBJ_CHECK_NETWORK.ONLINE.title3"));
          setMessage(getLabel("OBJ_CHECK_NETWORK.ONLINE.des3"));
        }, 15000);

        const closeTimer = setTimeout(() => {
          setOpen(false);
        }, 20000);

        // lưu lại trạng thái hiện tại cho lần kế tiếp
        prevIsOnline.current = isOnline;

        return () => {
          clearTimeout(updateTimer2);
          clearTimeout(updateTimer3);
          clearTimeout(closeTimer);
        };
      } else {
        // --- từ online sang offline
        setTitle(getLabel("OBJ_CHECK_NETWORK.OFFLINE.title1"));
        setMessage(getLabel("OBJ_CHECK_NETWORK.OFFLINE.des1"));
        setOpen(true);

        const updateTimer2 = setTimeout(() => {
          setTitle(getLabel("OBJ_CHECK_NETWORK.OFFLINE.title2"));
          setMessage(getLabel("OBJ_CHECK_NETWORK.OFFLINE.des2"));
        }, 5000);

        const closeTimer = setTimeout(() => {
          setOpen(false);
        }, 10000);

        // lưu lại trạng thái hiện tại cho lần kế tiếp
        prevIsOnline.current = isOnline;

        return () => {
          clearTimeout(updateTimer2);
          clearTimeout(closeTimer);
        };
      }
    }
    prevIsOnline.current = isOnline;
  }, [isOnline, currentPath]);

  return (
    <QueryClientProvider client={queryClient}>
      <LoadingCheckNetworkDialog open={open} title={title} message={message} onClose={() => setOpen(false)} />
      <AppLoading />
      <HashRouter>
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route path={PathConstant.MN_APPLICATION} element={<ApplicationListPage />} />
            <Route path={PathConstant.MN_DEVICE} element={<DeviceListPage />} />
            <Route path={PathConstant.MN_REQUEST} element={<InternalRequestListPage />} />
            <Route path={PathConstant.MN_RESPONSE} element={<ExternalRequestListPage />} />
            <Route path={PathConstant.MN_ID_SEARCH} element={<IdentitySearchListPage />} />
            <Route path={PathConstant.MN_SAILOR_SEARCH} element={<SailorSearchListPage />} />
            <Route path={PathConstant.MN_SHIP_SEARCH} element={<ShipSearchListPage />} />
            <Route path={PathConstant.MN_ID_RESPONSE} element={<IdentityResponseListPage />} />
            <Route path={PathConstant.MN_SAILOR_RESPONSE} element={<SailorResponseListPage />} />
            <Route path={PathConstant.MN_VIOLATION_RESPONSE} element={<ViolationResponseListPage />} />
            <Route path={PathConstant.MN_USER} element={<UserListPage />} />
            <Route path={PathConstant.MN_USER_NEW} element={<UserAddPage />} />
            <Route path={PathConstant.PROFILE} element={<ProfilePage />} />
            <Route path="" element={<Navigate to={PathConstant.MN_REQUEST} />} />
          </Route>
          <Route path="/demo" element={<DemoPage />} />
          <Route path={PathConstant.LOGIN} element={<LoginPage />} />
        </Routes>
      </HashRouter>
    </QueryClientProvider>
  );
}
